import React, {useState, useEffect, useContext, useCallback} from 'react';
import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {useDocumentId, useEngagementId} from '../../../../../../util/customHooks';
import {labels, GetCardTypeForBody, currentResource, isNullOrUndefined} from '../../../../../../util/utils';
import {aresTypeEightGuidanceFilter} from '../../../../../Main/Ares/Tasks/TaskView/AresBodies/aresBodyConfig';
import BodyKnowledgeDescription from '../../BodyKnowledgeDescription';
import Loader from '../../../../../Common/LoadingIndicator/LoadingIndicator';
import ProgressBar from '../../../../BaseComponentWrappers/MotifProgressBar';
import {
	getRiskById,
	updateStoreRelateScotToRisk,
	updateStoreUnRelateScotToRisk,
	unRelateSCOTToRisk
} from '../../../../../../actions/Risk/riskactions';
import EllipsesControl from '../../../../../Common/EllipsesControl/EllipsesControl';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import {IconoirCustomEstimate} from '@ey/motif-react-wrapper/assets/icons/custom';
import {IconoirWarningTriangle} from '@ey-xd/motif-icon';
import MotifButton from '@ey/motif-react-wrapper/Button';
import RelateScotModal from '../../../../../Common/scotPlacemat/RelateScotModal';
import _ from 'lodash';
import {useSmartEvidenceSourceId, useSmartEvidenceSourceEntityId} from '../../../../../../util/Ares/customHooks';
import {Entity, promiseAreas} from '../../../../../../util/uiconstants';
import ManageScots from '../../../../SCOTs/ManageScots/ManageScots';
import {DocumentTypeIdContext} from '../../../../../Main/Forms/CanvasFormContainer';
import {getUnresolvedCommentsCount} from '../../../../../../actions/ValidationControl/validationcontrolactions';
import {usePromiseTracker} from 'react-promise-tracker';

export default function RelateEstimateToSCOT(props) {
	const {isAresView, bodyContent, headerId, sectionId, bodyId, getBodiesBySectionId, isSnapshotView} = props;

	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const risk = useSelector((state) => state.risk);
	const scots = useSelector((state) => state.risk?.scots);
	const [isLoading, setIsLoading] = useState(false);
	const documentId = useDocumentId();
	const [showRelateScotModal, setShowRelateScotModal] = useState(false);
	const [scotList, setScotList] = useState([]);
	const riskId = useSmartEvidenceSourceId(documentId);
	const smartEvidenceSourceEntityId = useSmartEvidenceSourceEntityId(documentId);
	const [scotModel, setScotModel] = useState(false);
	const documentTypeId = useContext(DocumentTypeIdContext);
	const {promiseInProgress} = usePromiseTracker({area: promiseAreas.RISK_GET_BY_ID_AREA});

	useEffect(() => {
		if (!isNullOrUndefined(engagementId) && !isNullOrUndefined(riskId) && scotModel && !promiseInProgress) {
			setIsLoading(true);
			setScotModel(false);
			dispatch(
				getRiskById(engagementId, riskId, {includeAccounts: true, includeRelatedRisks: true, includeScots: true})
			).finally(() => {
				setIsLoading(false);
				getValidations();
			});
		} else {
			setIsLoading(false);
		}
	}, [scotModel]);

	useEffect(() => {
		if (!isNullOrUndefined(engagementId) && !isNullOrUndefined(riskId) && _.isEmpty(risk) && !promiseInProgress) {
			setIsLoading(true);
			setScotModel(false);
			dispatch(
				getRiskById(engagementId, riskId, {includeAccounts: true, includeRelatedRisks: true, includeScots: true})
			).finally(() => setIsLoading(false));
		} else {
			setIsLoading(false);
		}
	}, [risk]);

	useEffect(() => {
		if (scots?.length > 0) {
			const listScots = scots.map((scot) => {
				return {...scot, isLoading: false};
			});
			setScotList(listScots);
		}
	}, [scots]);

	const getValidations = useCallback(() => {
		if (documentId) {
			dispatch(getUnresolvedCommentsCount(engagementId, documentId, documentTypeId));
		}
	}, [engagementId, documentId, documentTypeId]);

	const relateOrUnRelateScotToRisk = (checked, scot) => {
		if (checked) {
			let scotDetails = {
				isEstimate: scot.isEstimate,
				isNonRoutine: scot.isNonRoutine,
				isRoutine: scot.isRoutine,
				riskID: scot.riskID,
				scotID: scot.scotID,
				scotName: scot.scotName,
				strategyTypeID: scot.strategyTypeID
			};
			let payload = {
				EngagementId: engagementId,
				riskId: scot.riskID,
				scotData: scotDetails
			};
			dispatch(updateStoreRelateScotToRisk(payload));
		} else {
			let payload = {
				EngagementId: engagementId,
				riskID: scot.riskID,
				scotID: scot.scotID
			};
			dispatch(updateStoreUnRelateScotToRisk(payload));
		}
		getValidations();
	};

	function onClickRemoveScot(scot) {
		if (!isNullOrUndefined(scot)) {
			let scotIndex = scotList.findIndex((item) => item.scotID === scot.scotID);
			let updatedList = [...scotList];
			updatedList[scotIndex].isLoading = true;
			setScotList(updatedList);

			dispatch(unRelateSCOTToRisk(engagementId, scot.riskID, scot.scotID))
				.then(() => {
					let scotData = {
						riskID: scot.riskID,
						scotID: scot.scotID
					};
					dispatch(updateStoreUnRelateScotToRisk(scotData));
				})
				.finally(() => {
					let updatedList = [...scotList];
					updatedList[scotIndex].isLoading = false;
					getValidations();
					setScotList(updatedList);
				});
		}
	}

	const formBodyBorder = GetCardTypeForBody(bodyContent, isAresView);

	const onIconClicked = (e) => {
		e.stopPropagation();
	};

	function getStrategyType(scot) {
		return scot.strategyTypeID
			? currentResource.strategyTypeCheck[scot.strategyTypeID]
			: labels.relateEstimate.notSelected;
	}

	function getScotType(scot) {
		if (scot.isRoutine) {
			return labels.relateEstimate.routine;
		} else if (scot.isNonRoutine) {
			return labels.relateEstimate.nonRoutine;
		} else {
			return labels.relateEstimate.notSelected;
		}
	}

	const scotListRender = () => {
		return (
			scotList?.length > 0 &&
			scotList.map((scot) => {
				return (
					<section className="list scot-list" key={scot.id}>
						{scot.isLoading ? (
							<section className="loader">
								<ProgressBar isIndeterminate circle hideLabel />
								{/* <Loader show={scot.isLoading} view="inlineView" /> */}
							</section>
						) : (
							<>
								<section className="scot-name">
									<EllipsesControl content={scot.scotName} tooltip={scot.scotName} noOfLines={1} />
								</section>
								<section className="has-estimate" title={labels.relateEstimate.estimate}>
									{scot.isEstimate ? (
										<MotifIcon iconFunction={IconoirCustomEstimate} title={labels.relateEstimate.estimate} size="20" />
									) : (
										<></>
									)}
								</section>
								<section className="scot-strategy">
									<EllipsesControl content={getStrategyType(scot)} tooltip={getStrategyType(scot)} noOfLines={1} />
								</section>
								<section className="scot-type">
									<EllipsesControl content={getScotType(scot)} tooltip={getScotType(scot)} noOfLines={1} />
								</section>

								<section className="scot-remove button-group remove-button">
									<MotifButton
										className="build-step-link-btn btn"
										variant={'text-alt'}
										onClick={() => onClickRemoveScot(scot)}
									>
										{labels.relateEstimate.remove}
									</MotifButton>
								</section>
							</>
						)}
					</section>
				);
			})
		);
	};

	return (
		<StyledScot>
			{isLoading ? (
				<Loader show={isLoading} />
			) : (
				<section>
					<section className={`SectionBodyWrapper ${formBodyBorder}`}>
						<BodyKnowledgeDescription
							onIconClicked={onIconClicked}
							disabled={false}
							guidanceFilter={aresTypeEightGuidanceFilter}
							isAresView
							isSnapshotView={isSnapshotView}
							bodyModel={bodyContent}
							engagementId={engagementId}
							documentId={documentId}
							headerId={headerId}
							sectionId={sectionId}
							bodyId={bodyId}
							getBodiesBySectionId={getBodiesBySectionId}
							showRelateDocumentsIcon={true}
							showCommentsIcon={true}
						/>
					</section>
					{isNullOrUndefined(riskId) ||
					isNullOrUndefined(smartEvidenceSourceEntityId) ||
					smartEvidenceSourceEntityId !== Entity.Estimate ? (
						<section className="norecord">
							<section className="no-records-section motif-body2-default-light">{labels.relateEstimate.noEstimate}</section>
						</section>
					) : (
						<>
							<section className="button-container">
								<MotifButton
									className="build-step-link-btn btn"
									variant={'text-alt'}
									onClick={() => setShowRelateScotModal(true)}
								>
									{labels.relateEstimate.relateSCOTs}
								</MotifButton>
								<span className="separator">|</span>
								<ManageScots onClose={() => setScotModel(true)} />
							</section>
							<section className="relatescot">
								{scots?.length > 0 ? (
									<>
										<section className="scot-list-header">
											<section className="title scot-label">{labels.relateEstimate.scot}</section>
											<section className="title scot-strategy">{labels.relateEstimate.strategy}</section>
											<section className="title scot-type">{labels.relateEstimate.type}</section>
											<section className="title scot-remove">&nbsp;</section>
										</section>
										<>
											<section className="scot-list-container customScrollbar">{scotListRender()}</section>
										</>
									</>
								) : (
									<section className="no-records-section">
										<MotifIcon iconFunction={IconoirWarningTriangle} strokeWidth="2" size="20" />
										<section className="motif-body2-default-light">
											{labels.relateEstimate.noSCOT}
											<MotifButton
											className="build-step-link-btn btn placeholderlink"
											variant={'text-alt'}
											onClick={() => setShowRelateScotModal(true)}
											>
												{labels.relateEstimate.relateSCOTs}
											</MotifButton>
											{labels.relateEstimate.noSCOTmsg}
										</section>
									</section>
								)}
							</section>
						</>
					)}
				</section>
			)}
			{showRelateScotModal && (
				<RelateScotModal
					riskId={riskId}
					riskName={risk?.riskName}
					isModalOpen={showRelateScotModal}
					engagementId={engagementId}
					onClickRelateModal={() => setShowRelateScotModal(false)}
					handleCheckChange={relateOrUnRelateScotToRisk}
				/>
			)}
		</StyledScot>
	);
}

const StyledScot = styled.section`
	.relatescot,
	.norecord {
		background: var(--neutrals-00white);
		border-radius: var(--px-8);
		border: 1px solid var(--neutrals-200);
		margin-bottom: var(--px-16);
	}
	.scot-list-header {
		display: flex;
		border-radius: var(--px-8) var(--px-8) var(--px-0) var(--px-0);
		border-bottom: var(--px-1) solid var(--neutrals-300);
		background: var(--neutrals-25);
		> .title {
			font-size: var(--px-12);
			font-weight: 400;
			color: var(--neutrals-900);
			padding: var(--px-16);
		}
		.scot-label {
			width: 64%;
		}
		.scot-type {
			width: 13%;
		}

		.scot-strategy {
			width: 13%;
		}

		.scot-remove {
			width: 10%;
		}
	}

	.scot-list-container {
		max-height: var(--px-580);
		overflow: auto;
		.list {
			font-size: var(--px-14);
			font-weight: 300;
			color: var(--neutrals-900);
			display: flex;
			align-items: center;
			height: var(--px-62);
			&:hover {
				background: var(--neutrals-50) !important;
			}
		}

		.scot-list {
			.loader {
				width: 100%;
				height: 100%;
				text-align: center;
				height: var(--px-58);
				.motif-progress-bar-wrapper {
					width: 100%;
					height: 100%;
					svg {
						width: var(--px-32);
					}
				}
			}
			&:nth-child(odd) {
				background: var(--neutrals-00white);
				&:last-child {
					border-radius: var(--px-0) var(--px-0) var(--px-8) var(--px-8);
				}
			}
			&:nth-child(even) {
				background: var(--neutrals-25);
				&:last-child {
					border-radius: var(--px-0) var(--px-0) var(--px-8) var(--px-8);
				}
			}
			.scot-name {
				width: 56%;
				padding: var(--px-16);
			}
			.scot-type,
			.scot-strategy {
				width: 13%;
				padding: var(--px-16);
			}

			&:hover {
				.scot-remove {
					visibility: visible !important;
				}
			}
			.scot-remove {
				width: 10%;
				padding: var(--px-16);
				margin-left: auto;
				visibility: hidden !important;
			}
			.has-estimate {
				width: 8% !important;
				text-align: center;
				padding: var(--px-16);
				display: grid;
				.motif-icon {
					svg {
						height: var(--px-24);
						width: var(--px-24);
						path {
							fill: var(--neutrals-900);
						}
					}
				}
				svg {
					height: 100%;
					max-height: 100%;
					overflow: visible;
				}
			}
		}
	}

	.no-records-section {
		padding: var(--px-80) var(--px-0);
		display: flex;
		margin: 0 auto;
		justify-content: center;
		place-items: center;
		color: var(--neutrals-700);
		& > *{
			display: flex;
		}
		.placeholderlink{
			margin: 0 var(--px-5);
			line-height: inherit;
		}
		svg{
			margin-right: var(--spacing-small);
			path{
				color: var(--orange-500);
			}
		}
	}

	.button-container {
		display: flex;
		justify-content: flex-end;
		padding-bottom: var(--spacing-small);
		font-size: var(--px-14);
		font-weight: 400;
		color: var(--blue-600);

		.separator {
			display: inline-block;
			padding: 0 var(--px-8);
			font-size: var(--px-14);
			font-weight: 400;
			color: var(--neutrals-700);
		}
	}
`;
